import React from "react";
import {
  AlertTriangle,
  FileWarning,
  Copy,
  Lock,
  FileX,
  Image as ImageIcon,
  ScanLine,
  HelpCircle,
  Info,
} from "lucide-react";

/**
 * Shown at the top of the dashboard when the backend flagged some files
 * as duplicates, non-food invoices, unreadable, or partial reads.
 *
 * Props: { warnings: [{filename, type, message}] }
 *
 * Each type gets its own icon + colour + helpful "Why?" tooltip + a
 * one-line "Tip:" explaining how to fix it so the user can retry
 * successfully instead of just being told something's wrong.
 */
const TYPE_META = {
  duplicate: {
    label: (n) => `${n} duplicate${n > 1 ? "s" : ""}`,
    icon: Copy,
    accent: "#BA7517",
    tip: "We only count a transaction once. Re-uploading the same invoice won't change your report.",
    why: "The invoice fingerprint matches one you've already processed.",
  },
  unrelated: {
    label: (n) => `${n} not a food invoice`,
    icon: FileWarning,
    accent: "#E24B4A",
    tip: "Upload screenshots or PDFs of orders from Blinkit, Zepto, Swiggy, Zomato, BigBasket, Licious, Instamart, or similar food/grocery apps.",
    why: "Couldn't detect grocery or food items — looks like a non-food receipt, a chat screenshot, or a random image.",
  },
  partial_read: {
    label: (n) => `${n} partially read`,
    icon: ScanLine,
    accent: "#FF9560",
    tip: "Take a full-screen screenshot of the order page — avoid cropped views, zoomed-in cards, or blurred captures.",
    why: "Our OCR extracted some text, but not enough line-items to trust the order total.",
  },
  image_unreadable: {
    label: (n) => `${n} unreadable image${n > 1 ? "s" : ""}`,
    icon: ImageIcon,
    accent: "#E24B4A",
    tip: "Re-take the screenshot at a higher resolution, full-screen, and make sure the text is readable.",
    why: "The image was too low-res, blurry, or truncated for us to extract anything.",
  },
  password_protected: {
    label: (n) => `${n} locked PDF${n > 1 ? "s" : ""}`,
    icon: Lock,
    accent: "#BA7517",
    tip: "Open the PDF in a reader, unlock it, then export / print-to-PDF a fresh unlocked copy and re-upload.",
    why: "The PDF is encrypted. We can't extract text from password-protected files.",
  },
  empty: {
    label: (n) => `${n} empty PDF${n > 1 ? "s" : ""}`,
    icon: FileX,
    accent: "#BA7517",
    tip: "Make sure you're downloading the order invoice PDF, not a blank template or a confirmation redirect.",
    why: "The PDF opened fine but contained no extractable text.",
  },
  unreadable: {
    label: (n) => `${n} unreadable PDF${n > 1 ? "s" : ""}`,
    icon: FileX,
    accent: "#E24B4A",
    tip: "Try re-downloading the invoice from the original app and uploading the fresh copy.",
    why: "The PDF is corrupted, scanned as an image with no OCR layer, or in an unsupported format.",
  },
};

const UNKNOWN_META = {
  label: (n) => `${n} issue${n > 1 ? "s" : ""}`,
  icon: HelpCircle,
  accent: "#888",
  tip: "Try uploading a different file or contact support@solutionist.co.in.",
  why: "Something went wrong processing this file.",
};

export default function UploadWarningsBanner({ warnings }) {
  if (!warnings || warnings.length === 0) return null;

  // Group by type, preserving the raw order within each group for stable UI.
  const groups = warnings.reduce((acc, w) => {
    const t = w.type || "unknown";
    (acc[t] = acc[t] || []).push(w);
    return acc;
  }, {});

  // Stable rendering order.
  const ORDER = [
    "duplicate",
    "unrelated",
    "partial_read",
    "image_unreadable",
    "password_protected",
    "empty",
    "unreadable",
  ];
  const orderedTypes = [
    ...ORDER.filter((t) => groups[t]),
    ...Object.keys(groups).filter((t) => !ORDER.includes(t)),
  ];

  return (
    <div
      className="cartiq-card border-l-[3px] border-l-[#BA7517]"
      style={{ background: "rgba(186,117,23,0.06)" }}
      data-testid="upload-warnings-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-[#BA7517]/15 border border-[#BA7517]/40 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-4 h-4 text-[#BA7517]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#BA7517] font-bold mb-1">
            — Upload notes
          </div>
          <h3 className="font-display uppercase text-xl mb-2">
            {warnings.length} {warnings.length === 1 ? "file needs" : "files need"} your attention
          </h3>
          <p className="text-sm text-[#888] mb-4 leading-relaxed">
            Your report is built from the rest. These files were set aside —
            here's exactly why + how to fix each one:
          </p>

          <div className="space-y-4">
            {orderedTypes.map((type) => {
              const meta = TYPE_META[type] || UNKNOWN_META;
              const Icon = meta.icon;
              const items = groups[type];
              return (
                <div key={type} data-testid={`warning-group-${type}`}>
                  <div
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold mb-1.5"
                    style={{ color: meta.accent }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{meta.label(items.length)}</span>
                    <span
                      className="group relative inline-flex items-center cursor-help"
                      aria-label="Why?"
                      data-testid={`warning-why-${type}`}
                    >
                      <HelpCircle className="w-3 h-3 text-[#666]" />
                      <span
                        className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute left-5 top-1/2 -translate-y-1/2 z-10 w-64 bg-[#14141A] border border-[#2A2A35] px-3 py-2 text-[11px] normal-case tracking-normal leading-relaxed text-[#D0CFC9] shadow-lg"
                      >
                        <span className="text-[#F0EDE8] font-bold block mb-0.5">Why?</span>
                        {meta.why}
                      </span>
                    </span>
                  </div>

                  <ul className="space-y-1 mb-2">
                    {items.map((w, i) => (
                      <li
                        key={i}
                        className="text-[13px] text-[#F0EDE8] flex items-start gap-2 break-all"
                        data-testid={`warning-item-${type}-${i}`}
                        title={w.message}
                      >
                        <span className="text-[#555] shrink-0">•</span>
                        <span className="min-w-0">
                          <span className="font-bold">{w.filename}</span>
                          <span className="text-[#888]"> — {w.message}</span>
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div
                    className="flex items-start gap-2 bg-[#14141A]/60 border-l-2 px-2.5 py-1.5 text-[11px] leading-relaxed"
                    style={{ borderLeftColor: meta.accent }}
                    data-testid={`warning-tip-${type}`}
                  >
                    <Info
                      className="w-3 h-3 shrink-0 mt-[3px]"
                      style={{ color: meta.accent }}
                    />
                    <span className="text-[#D0CFC9]">
                      <span className="text-[#F0EDE8] font-bold">Tip: </span>
                      {meta.tip}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
